<template>
  <div class="flex flex-col h-full">
    <!-- Contrôles externes -->
    <div class="bg-white p-4 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-4">
          <button
            class="btn-control"
            :disabled="
              !supportStore.currentSupport?.products ||
                supportStore.currentSupport.products.length <= 1
            "
            @click="decreaseProducts"
          >
            <span class="text-xl">-</span>
          </button>
          <span class="text-lg font-medium">{{ nbProducts }} produit{{ nbProducts > 1 ? "s" : "" }}</span>
          <button
            class="btn-control"
            :disabled="
              !supportStore.currentSupport?.products ||
                supportStore.currentSupport.products.length >= 8
            "
            @click="increaseProducts"
          >
            <span class="text-xl">+</span>
          </button>
        </div>

        <!-- Boutons de contrôle -->
        <div class="flex items-center gap-2">
          <!-- Contrôles de zoom -->
          <div class="flex items-center gap-2">
            <button
              class="btn-control"
              :disabled="zoomLevel <= 50"
              title="Zoom arrière"
              @click="adjustZoom(-10)"
            >
              <span class="text-sm">🔍-</span>
            </button>
            <span class="text-sm font-medium">{{ zoomLevel }}%</span>
            <button
              class="btn-control"
              :disabled="zoomLevel >= 200"
              title="Zoom avant"
              @click="adjustZoom(10)"
            >
              <span class="text-sm">🔍+</span>
            </button>
          </div>

          <!-- Bouton aperçu -->
          <button
            class="preview-toggle"
            :class="{ active: isPreviewMode }"
            @click="isPreviewMode = !isPreviewMode"
          >
            <span class="icon">👁️</span>
            {{ isPreviewMode ? "Mode édition" : "Aperçu" }}
          </button>

          <!-- Bouton générer HTML -->
          <button
            class="btn-control bg-green-600 text-white hover:bg-green-700"
            title="Générer le HTML complet pour l'envoi"
            @click="generateAndDownloadHtml"
          >
            <span class="text-sm">📧 HTML</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Affiche -->
    <div
      class="support-canvas relative bg-white shadow-lg"
      :class="{ 'preview-mode': isPreviewMode }"
    >
      <!-- En-tête avec logo -->
      <div
        v-if="showHeader"
        class="support-header bg-white border-b border-gray-200"
        @dragover.prevent="isDraggingOver = 'header'"
        @dragleave.prevent="isDraggingOver = null"
        @drop.prevent="onHeaderDrop"
      >
        <img :src="headerImage" alt="Horizon Vert" class="h-full">
      </div>
      <!-- Zone de titre -->
      <div
        class="support-title-container p-2"
        :style="{ backgroundColor: brand === 'fendt' ? '#005e1e' : '#000000' }"
      >
        <h2
          class="support-title-text text-center text-xl font-bold"
          style="color: #ffffff"
          contenteditable="true"
          @blur="updateTitle"
          v-text="titleText"
        />
      </div>

      <!-- Zone de contenu -->
      <div class="support-content">
        <div class="products-container">
          <div
            v-for="index in nbProducts"
            :key="`product-zone-${index}`"
            class="product-zone w-full"
            @dragover.prevent="isDraggingOver = index - 1"
            @dragleave.prevent="isDraggingOver = null"
            @drop.prevent="onDrop($event, index - 1)"
          >
            <template v-if="getProduct(index - 1) && getProduct(index - 1)?.id">
              <ProductBlockEmail
                :key="`product-${getProduct(index - 1)?.id}-${index}`"
                :product="getProduct(index - 1)"
                :variant="'columnOne'"
                :is-preview="isPreviewMode"
                :font-family="selectedFont"
                :font-size="selectedSize"
                :position="index % 2 === 0 ? 1 : 2"
                @remove="removeProduct(index - 1)"
                @update:product="handleProductUpdate(index - 1, $event)"
              />
            </template>
            <div v-else class="empty-product-zone">
              <div class="text-center text-gray-500 py-8">
                <p>Zone de produit vide</p>
                <p class="text-sm">
                  Déposez un produit ici
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pied de page -->
      <div class="support-footer">
        <!-- Bandeau de validité -->
        <div
          class="text-white text-center py-3 text-lg font-medium"
          :style="{ backgroundColor: validityBackgroundColor }"
        >
          <span
            :style="{ color: validityTextColor }"
            contenteditable="true"
            @blur="updateValidityText"
            v-text="validityText"
          />
        </div>

        <div class="bg-white p-8 border-t border-gray-200">
          <div class="flex flex-col items-center gap-6">
            <!-- Zone de repiquage -->
            <template v-if="selectedRepiquage">
              <div
                class="w-[500px] h-[240px] bg-white rounded-lg border border-gray-300 flex items-center justify-center overflow-hidden"
              >
                <div
                  class="relative w-full h-full flex items-center justify-center"
                >
                  <div
                    class="w-full h-full flex items-center justify-center overflow-hidden"
                  >
                    <img
                      :src="selectedRepiquage.logo"
                      :alt="selectedRepiquage.titre"
                      class="max-w-[400px] w-auto h-auto object-contain transition-transform duration-200"
                      :style="{
                        transform: `scale(${selectedRepiquage.zoom || 1})`,
                        transformOrigin: 'center center',
                      }"
                    >
                  </div>
                  <button
                    v-if="!isPreviewMode"
                    class="absolute top-1 right-1 text-red-600 hover:text-red-700 bg-white rounded-full p-1 shadow-sm z-10"
                    @click="removeRepiquage"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </template>
            <button
              v-else
              class="w-[400px] bg-white rounded-lg border-2 border-dashed border-gray-300 p-4 text-center cursor-pointer hover:border-brand transition-colors"
              :class="{ 'preview-mode-button': isPreviewMode }"
              @click="showSignaturesModal = true"
            >
              <div class="flex items-center justify-center">
                <div
                  class="rounded-full border-2 border-brand w-8 h-8 flex items-center justify-center mb-2"
                >
                  <span class="text-brand text-2xl">+</span>
                </div>
              </div>
              <span class="text-gray-600">Ajouter un repiquage adresse(s)</span>
            </button>

            <!-- Réseaux sociaux -->
            <div class="flex items-center gap-4">
              <!-- Lien vers le site web du concessionnaire si rempli dans le profil -->
              <a 
                v-if="dealerWebsite" 
                :href="dealerWebsite" 
                target="_blank" 
                rel="noopener noreferrer"
                class="text-brand hover:opacity-80"
                :title="'Visiter notre site web'"
              >
                <svg
                  class="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                  <path d="M2 12h20" stroke="currentColor" stroke-width="2" />
                  <path
                    d="M12 2a15 15 0 0 1 0 20a15 15 0 0 1 0-20z"
                    stroke="currentColor"
                    stroke-width="2"
                    fill="none"
                  />
                </svg>
              </a>
              <a href="#" class="text-brand hover:opacity-80">
                <svg
                  class="w-6 h-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                  />
                </svg>
              </a>
              <a href="#" class="text-brand hover:opacity-80">
                <svg
                  class="w-6 h-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M12 2c2.717 0 3.056.01 4.122.06 1.065.05 1.79.217 2.428.465.66.254 1.216.598 1.772 1.153a4.908 4.908 0 0 1 1.153 1.772c.247.637.415 1.363.465 2.428.047 1.066.06 1.405.06 4.122 0 2.717-.01 3.056-.06 4.122-.05 1.065-.218 1.79-.465 2.428a4.883 4.883 0 0 1-1.153 1.772 4.915 4.915 0 0 1-1.772 1.153c-.637.247-1.363.415-2.428.465-1.066.047-1.405.06-4.122.06-2.717 0-3.056-.01-4.122-.06-1.065-.05-1.79-.218-2.428-.465a4.89 4.89 0 0 1-1.772-1.153 4.904 4.904 0 0 1-1.153-1.772c-.248-.637-.415-1.363-.465-2.428C2.013 15.056 2 14.717 2 12c0-2.717.01-3.056.06-4.122.05-1.066.217-1.79.465-2.428a4.88 4.88 0 0 1 1.153-1.772A4.897 4.897 0 0 1 5.45 2.525c.638-.248 1.362-.415 2.428-.465C8.944 2.013 9.283 2 12 2zm0 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm6.5-.25a1.25 1.25 0 1 0-2.5 0 1.25 1.25 0 0 0 2.5 0zM12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"
                  />
                </svg>
              </a>
              <a href="#" class="text-brand hover:opacity-80">
                <svg
                  class="w-6 h-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                  />
                </svg>
              </a>
            </div>

            <!-- Logo AGCO - seulement pour la marque AGCO -->
            <div v-if="brand === 'agco'" class="mb-4">
              <img src="/images/logo-agco.svg" alt="AGCO" class="h-12">
            </div>

            <!-- Mentions légales -->
            <div
              class="text-center text-sm text-gray-600 space-y-2 w-3/5 mx-auto"
            >
              <p>
                Validité des textes sous réserve d'erreurs d'impression. Les
                articles sont assujettis à une TVA de 20%. Port et montage non
                inclus. {{ brandName }} est une marque AGCO SA.
              </p>
              <p>
                Photos non contractuelles. Dans la limite des stocks
                disponibles.
              </p>
              <p>
                <a
                  href="#"
                  class="text-brand underline hover:opacity-80"
                  @click="unsubscribe"
                >Se désabonner</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de sélection des signatures -->
    <div
      v-if="showSignaturesModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div
        class="bg-white rounded-lg p-6 w-[600px] max-h-[80vh] overflow-y-auto"
      >
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold">
            Sélectionner une signature
          </h3>
          <button
            class="text-gray-500 hover:text-gray-700"
            @click="showSignaturesModal = false"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Contrôle de zoom -->
        <div
          v-if="selectedSignatureForZoom"
          class="mb-4 p-4 border rounded-lg bg-gray-50"
        >
          <div class="flex items-center justify-between mb-2">
            <label class="text-sm font-medium text-gray-700">Ajuster la taille</label>
            <span class="text-sm text-gray-500">{{ zoomLevel }}%</span>
          </div>
          <input
            v-model="zoomLevel"
            type="range"
            min="50"
            max="150"
            step="5"
            class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          >
          <div class="mt-4 flex justify-center">
            <img
              :src="selectedSignatureForZoom.logo"
              :alt="selectedSignatureForZoom.titre"
              class="max-h-[150px] object-contain transition-transform duration-200"
              :style="{ transform: `scale(${zoomLevel / 100})` }"
            >
          </div>
          <div class="mt-4 flex justify-end gap-2">
            <button
              class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              @click="cancelZoom"
            >
              Annuler
            </button>
            <button
              class="px-4 py-2 text-sm bg-brand text-white rounded hover:bg-[#8c0014]"
              @click="confirmZoom"
            >
              Appliquer
            </button>
          </div>
        </div>

        <div v-else class="grid grid-cols-2 gap-4">
          <div
            v-for="signature in userSignatures"
            :key="signature.id"
            class="border rounded-lg p-4 cursor-pointer hover:border-brand transition-colors"
            @click="startZoom(signature)"
          >
            <img
              :src="signature.logo"
              :alt="signature.titre"
              class="w-full h-32 object-contain mb-2"
            >
            <p class="text-sm text-gray-600 truncate">
              {{ signature.titre }}
            </p>
          </div>
        </div>

        <div v-if="userSignatures.length === 0" class="text-center py-8">
          <p class="text-gray-500">
            Aucune signature disponible
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import api from "@/api/api";
import { useAuthStore } from "@/stores/auth";
import { useProductCustomizationStore } from "@/stores/supports/useProductCustomizationStore";
import { useSupportStore } from "@/stores/supports/useSupportStore";
import { generateCompleteEmailHtml } from "@/utils/emailUtils";
import { computed, defineExpose, onMounted, ref } from "@vue/runtime-core";
import domtoimage from "dom-to-image-more";
import { jsPDF } from "jspdf";
import { HIGH_QUALITY_SCALE, PDF_IMAGE_COMPRESSION } from "@/utils/pdfQualityConfig";
import ProductBlockEmail from "../blocks/ProductBlockEmail.vue";
const supportStore = useSupportStore();
const productCustomizationStore = useProductCustomizationStore();
const authStore = useAuthStore();
const isDraggingOver = ref<number | "header" | null>(null);
const selectedFont = ref("Arial");
const selectedSize = ref("md");
const isPreviewMode = ref(false);
const headerImage = ref<string | undefined>(undefined);
const showHeader = ref(true);
const titleText = ref("Votre titre");
const validityText = ref("Offres valables du XX au XX mois ANNÉE");
const brand = localStorage.getItem("brand") || "agco";
const validityBackgroundColor = ref(brand === "fendt" ? "#005e1e" : "#000000");
const validityTextColor = ref("#FFFFFF");

interface ProductBlockEmailProduct {
  id: number;
  name: string;
  description: string;
  price: number;
  image: string;
  reference?: string;
  image_name?: string;
  base_price?: string;
  category_id?: number;
}

interface Signature {
  id: number;
  titre: string;
  logo: string;
  zoom?: number;
}

// Nombre total de slots de produits
const nbProducts = computed(() => {
  try {
    const products = supportStore.currentSupport?.products || [];
    // console.log("nbProducts computed - produits:", products);
    // console.log("nbProducts computed - longueur:", products.length);
    const length = Array.isArray(products) ? products.length : 0;
    return Math.max(length, 1);
  } catch (error) {
    console.error("Erreur dans nbProducts computed:", error);
    return 1;
  }
});

const brandName = computed(() => {
  const brand = localStorage.getItem("brand") || "agco";
  if (brand === "hv") return "Horizon Vert";
  if (brand === "valtra") return "Valtra Shop";
  if (brand === "fendt") return "Fendt";
  return "AGCO";
});

// Computed pour récupérer le site web du concessionnaire depuis le profil
const dealerWebsite = computed(() => {
  const website = authStore.user?.user_details?.website;
  if (website && website.trim()) {
    // S'assurer que l'URL commence par http:// ou https://
    if (!website.startsWith('http://') && !website.startsWith('https://')) {
      return `https://${website}`;
    }
    return website;
  }
  return null;
});

const getProduct = (index: number): ProductBlockEmailProduct | null => {
  try {
    const products = supportStore.currentSupport?.products || [];
    // console.log("getProduct - index:", index, "produits:", products);
    const product = products[index];
    // console.log("getProduct - produit trouvé:", product);
    if (!product || !product.id) return null;

    // Convertir le produit du store en type ProductBlockEmailProduct
    const convertedProduct = {
      id: Number(product.id) || 0,
      name: product.name || "",
      description: product.description || "",
      price: product.price || 0,
      image: product.image || "",
      reference: product.reference || "",
      image_name: product.image_name || "",
    };

    // Vérification finale de sécurité
    if (!convertedProduct.id) {
      console.warn("Produit invalide détecté:", product);
      return null;
    }

    // console.log("getProduct - produit converti:", convertedProduct);
    return convertedProduct;
  } catch (error) {
    console.error("Erreur dans getProduct:", error);
    return null;
  }
};

const increaseProducts = () => {
  // console.log("increaseProducts called");
  if (!supportStore.currentSupport) {
    // console.log("No current support");
    return;
  }
  const products = supportStore.currentSupport.products || [];
  // console.log("Current products:", products);

  if (products.length < 8) {
    const newProducts = [...products];
    newProducts.push(null);
    supportStore.currentSupport.products = newProducts;
    // console.log("Products increased to:", newProducts);
  } else {
    // console.log("Maximum products reached");
  }
};

const decreaseProducts = () => {
  // console.log("decreaseProducts called");
  if (!supportStore.currentSupport?.products) {
    // console.log("No products to decrease");
    return;
  }
  const products = supportStore.currentSupport.products;
  // console.log("Current products:", products);

  if (products.length > 1) {
    const newProducts = products.slice(0, -1);
    supportStore.currentSupport.products = newProducts;
    // console.log("Products decreased to:", newProducts);
  } else {
    // console.log("Minimum products reached");
  }
};

const removeProduct = (index: number) => {
  if (!supportStore.currentSupport?.products) return;
  supportStore.currentSupport.products[index] = null;
};

const onDrop = (event: DragEvent, index: number) => {
  isDraggingOver.value = null;
  const productData = event.dataTransfer?.getData("text/plain");
  if (!productData) return;

  try {
    const parsedData = JSON.parse(productData);
    // console.log("Drop event received for product");
    // console.log("Parsed product data:", parsedData);

    if (parsedData.type === "product" && parsedData.data) {
      const product = parsedData.data;

      // Vérification de sécurité pour le produit
      if (!product || !product.id) {
        console.warn("Produit invalide reçu:", product);
        return;
      }

      // Ajout des champs nécessaires comme dans SupportCanvas.vue
      if (product && !product.type && product.id) {
        product.type = "product";
        product.product_id = product.id;
      }

      // S'assurer que le support existe
      if (!supportStore.currentSupport) {
        console.warn("Aucun support actuel pour ajouter le produit");
        return;
      }

      // Gérer directement les éléments de la première page
      if (
        supportStore.currentSupport.pages &&
        supportStore.currentSupport.pages.length > 0
      ) {
        const firstPage = supportStore.currentSupport.pages[0];

        // S'assurer que le tableau elements existe
        if (!Array.isArray(firstPage.elements)) {
          firstPage.elements = [];
        }

        // S'assurer que l'index existe
        while (firstPage.elements.length <= index) {
          firstPage.elements.push(null);
        }

        firstPage.elements[index] = product;
        // console.log("Produit ajouté à la page:", product);

        // Mettre à jour le store
        supportStore.updateSupport({
          pages: supportStore.currentSupport.pages,
        });

        // Mettre à jour aussi le tableau products pour la compatibilité
        const products = supportStore.currentSupport.products || [];
        const newProducts = [...products];
        while (newProducts.length <= index) {
          newProducts.push(null);
        }
        newProducts[index] = product;
        supportStore.currentSupport.products = newProducts;
      }
    }
  } catch (error) {
    console.error("Invalid product data:", error);
  }
};

const handleProductUpdate = (
  index: number,
  product: ProductBlockEmailProduct
) => {
  if (!product || !product.id) {
    console.warn("Tentative de mise à jour avec un produit invalide:", product);
    return;
  }

  // Ajout des champs nécessaires comme dans SupportCanvas.vue
  const productWithType = {
    ...product,
    type: "product",
    product_id: product.id,
  } as any;

  // console.log("Product updated:", productWithType);

  // Gérer directement les éléments de la première page
  if (
    supportStore.currentSupport?.pages &&
    supportStore.currentSupport.pages.length > 0
  ) {
    const firstPage = supportStore.currentSupport.pages[0];

    // S'assurer que le tableau elements existe
    if (!Array.isArray(firstPage.elements)) {
      firstPage.elements = [];
    }

    const updatedElements = [...firstPage.elements];

    // S'assurer que l'index existe
    while (updatedElements.length <= index) {
      updatedElements.push(null);
    }

    updatedElements[index] = productWithType;

    const updatedPages = supportStore.currentSupport.pages.map((page: any) => {
      if (page.page_number === firstPage.page_number) {
        return { ...page, elements: updatedElements };
      }
      return page;
    });

    supportStore.updateSupport({
      pages: updatedPages,
    });

    // Mettre à jour aussi le tableau products pour la compatibilité
    const products = supportStore.currentSupport.products || [];
    const newProducts = [...products];
    while (newProducts.length <= index) {
      newProducts.push(null);
    }
    newProducts[index] = productWithType;
    supportStore.currentSupport.products = newProducts;
  }
};

// Helper pour construire l'URL
const getHeaderImageUrl = (image_name?: string) => {
  const apiUrl = import.meta.env.VITE_API_URL; // Utilisation de import.meta.env pour Vite
  return image_name ? `${apiUrl}/upl/headers/${image_name}` : undefined;
};

const showSignaturesModal = ref(false);
const userSignatures = ref<Signature[]>([]);
const selectedRepiquage = ref<Signature | null>(null);
const selectedSignatureForZoom = ref<Signature | null>(null);
const zoomLevel = ref(100);

// Méthode pour ajuster le zoom
const adjustZoom = (delta: number) => {
  try {
    const newZoom = zoomLevel.value + delta;
    if (newZoom >= 50 && newZoom <= 200) {
      zoomLevel.value = newZoom;
      applyZoom();
    }
  } catch (error) {
    console.error("Erreur lors de l'ajustement du zoom:", error);
  }
};

// Appliquer le zoom au canvas
const applyZoom = () => {
  try {
    const canvas = document.querySelector(".support-canvas") as HTMLElement;
    if (canvas) {
      canvas.style.transform = `scale(${zoomLevel.value / 100})`;
      canvas.style.transformOrigin = "top left";
    }
  } catch (error) {
    console.error("Erreur lors de l'application du zoom:", error);
  }
};

// Charger les signatures de l'utilisateur
const loadUserSignatures = () => {
  const storedSignatures = localStorage.getItem("user_signatures");
  if (storedSignatures) {
    userSignatures.value = JSON.parse(storedSignatures);
  }
};

// Sélectionner un repiquage
const selectRepiquage = (signature: Signature) => {
  selectedRepiquage.value = signature;
  showSignaturesModal.value = false;
  if (supportStore.currentSupport) {
    supportStore.currentSupport.repiquage = signature;
  }
};

// Supprimer le repiquage
const removeRepiquage = () => {
  selectedRepiquage.value = null;
  if (supportStore.currentSupport) {
    supportStore.currentSupport.repiquage = null;
  }
};

const startZoom = (signature: Signature) => {
  selectedSignatureForZoom.value = signature;
  zoomLevel.value = 100;
};

const cancelZoom = () => {
  selectedSignatureForZoom.value = null;
  zoomLevel.value = 100;
};

const confirmZoom = () => {
  if (selectedSignatureForZoom.value) {
    const signature = {
      ...selectedSignatureForZoom.value,
      zoom: zoomLevel.value / 100,
    };
    selectRepiquage(signature);
    selectedSignatureForZoom.value = null;
    zoomLevel.value = 100;
  }
};

onMounted(async () => {
  try {
    // console.log("SupportCanvaEmail mounted");
    // console.log("Current support:", supportStore.currentSupport);
    // console.log("Current products:", supportStore.currentSupport?.products);

    // Appliquer le zoom initial
    applyZoom();

    // S'assurer que le support a une structure valide
    if (!supportStore.currentSupport) {
      console.warn("Aucun support actuel trouvé");
      return;
    }

    // Charger le titre et les dates depuis le support
    if (supportStore.currentSupport.name) {
      titleText.value = supportStore.currentSupport.name;
    }
    if (supportStore.currentSupport.dates) {
      validityText.value = supportStore.currentSupport.dates;
    }

    // Initialiser les produits si nécessaire
    if (!supportStore.currentSupport.products) {
      supportStore.currentSupport.products = [];
    }

    // S'assurer que products est un tableau
    if (!Array.isArray(supportStore.currentSupport.products)) {
      supportStore.currentSupport.products = [];
    }
  } catch (error) {
    console.error("Erreur dans onMounted:", error);
  }

  // Charger les produits depuis les éléments des pages si c'est un emailing existant
  if (
    supportStore.currentSupport?.pages &&
    supportStore.currentSupport.pages.length > 0
  ) {
    const firstPage = supportStore.currentSupport.pages[0];
    // console.log("Première page trouvée:", firstPage);
    // console.log("Éléments de la page:", firstPage.elements);

    if (firstPage.elements && firstPage.elements.length > 0) {
      // Convertir les éléments en produits
      const productsFromElements = firstPage.elements
        .filter(
          (element: any) =>
            element && (element.type === "product" || element.product_image_id)
        )
        .map((element: any) => {
          // console.log("Watcher - conversion élément vers produit:", element);

          // Gérer le cas où product_image est un objet
          let imageUrl = "";
          if (
            typeof element.product_image === "object" &&
            element.product_image
          ) {
            imageUrl = element.product_image.image_name || "";
          } else if (typeof element.product_image === "string") {
            imageUrl = element.product_image;
          }

          return {
            id: element.product_id || element.product_image_id,
            name: element.name,
            description: element.description,
            price: parseFloat(element.price_ht || element.price_ttc || "0"),
            image: imageUrl,
            image_name: imageUrl,
            reference: element.reference,
            brand_id: element.brand_id || null,
            product_image_id: element.product_image_id,
          };
        });

      // console.log("Produits filtrés:", productsFromElements);

      if (productsFromElements.length > 0) {
        supportStore.currentSupport.products = productsFromElements;
        // console.log(
        //   "Produits chargés depuis les éléments:",
        //   productsFromElements
        // );
      } else {
        // Si aucun produit trouvé, initialiser avec un tableau vide
        supportStore.currentSupport.products = [];
        // console.log("Aucun produit trouvé, initialisation avec tableau vide");
      }
    } else {
      // Si pas d'éléments, initialiser avec un tableau vide
      supportStore.currentSupport.products = [];
    }
  } else {
    // Si pas de pages, initialiser avec un tableau vide
    if (!supportStore.currentSupport.products) {
      supportStore.currentSupport.products = [];
    }
  }

  try {
    const bannersResponse = await api.get(
      `/support_headers?groups.slug=${brand}&isActive=1`
    );
    const bannerData = Array.isArray(bannersResponse.data)
      ? bannersResponse.data
      : bannersResponse.data?.data || [];

    const featuredBanner = bannerData.find(
      (banner: { featured: boolean }) => banner.featured
    );

    if (featuredBanner?.image_name) {
      headerImage.value = getHeaderImageUrl(featuredBanner.image_name);
    } else if (bannerData.length > 0 && bannerData[0].image_name) {
      headerImage.value = getHeaderImageUrl(bannerData[0].image_name);
    }
  } catch (error) {
    console.error("Erreur lors du chargement des bannières:", error);
    headerImage.value = undefined;
  }

  // Charger les signatures
  await loadUserSignatures();

  // Charger le repiquage existant si présent
  if (supportStore.currentSupport?.repiquage) {
    selectedRepiquage.value = supportStore.currentSupport.repiquage;
  }
});

// Watcher pour recharger les produits quand le support change
// Suppression du watcher problématique qui causait des boucles infinies
// Les produits sont maintenant gérés directement dans les éléments des pages

const onHeaderDrop = (event: DragEvent) => {
  isDraggingOver.value = null;
  const bannerData = event.dataTransfer?.getData("text/plain");
  if (!bannerData) return;

  try {
    const parsedData = JSON.parse(bannerData);
    if (
      typeof parsedData === "object" &&
      parsedData !== null &&
      parsedData.type === "banner" &&
      parsedData.data?.image_name
    ) {
      headerImage.value = getHeaderImageUrl(parsedData.data.image_name);
    }
  } catch (error) {
    console.error("Invalid banner data:", error);
  }
};

const updateTitle = (event: FocusEvent) => {
  const target = event.target as HTMLElement;
  titleText.value = target.textContent || titleText.value;
};

const updateValidityText = (event: FocusEvent) => {
  const target = event.target as HTMLElement;
  validityText.value = target.textContent || validityText.value;
};

const unsubscribe = () => {
  // TODO: Implémenter la logique de désabonnement
  // console.log("Désabonnement...");
};

// Méthodes de génération d'image et PDF
const generateImage = async (): Promise<string> => {
  const canvasElement = document.querySelector(
    ".support-canvas"
  ) as HTMLElement;
  if (!canvasElement) {
    throw new Error("Élément canvas non trouvé");
  }

  // Sauvegarder l'état actuel
  const originalPreviewMode = isPreviewMode.value;
  const originalPreview = supportStore.isPreview;

  // Activer le mode aperçu pour la capture
  isPreviewMode.value = true;
  if (!originalPreview) {
    supportStore.togglePreview();
  }

  // Attendre que le DOM soit mis à jour
  await new Promise((resolve) => setTimeout(resolve, 500));

  try {
    // Ajouter la classe de capture PDF
    canvasElement.classList.add("pdf-capture-mode");

    // Sauvegarder les styles originaux
    const originalStyles = {
      backgroundColor: canvasElement.style.backgroundColor,
      height: canvasElement.style.height,
      overflow: canvasElement.style.overflow,
      maxHeight: canvasElement.style.maxHeight,
      position: canvasElement.style.position,
      top: canvasElement.style.top,
      left: canvasElement.style.left,
      transform: canvasElement.style.transform,
    };

    // Appliquer les styles pour la capture
    canvasElement.style.backgroundColor = "#ffffff";
    canvasElement.style.height = "auto";
    canvasElement.style.overflow = "visible";
    canvasElement.style.maxHeight = "none";
    canvasElement.style.position = "static";
    canvasElement.style.top = "auto";
    canvasElement.style.left = "auto";
    canvasElement.style.transform = "none";

    // S'assurer que tous les éléments sont visibles
    const allElements = canvasElement.querySelectorAll("*");
    allElements.forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.visibility = "visible";
        el.style.opacity = "1";
        el.style.display =
          el.style.display === "none" ? "block" : el.style.display;
      }
    });

    // Attendre que la hauteur soit recalculée
    await new Promise((resolve) => setTimeout(resolve, 200));

    // Calculer la hauteur réelle du contenu
    const contentHeight = canvasElement.scrollHeight;
    const contentWidth = canvasElement.scrollWidth;

    // console.log("Dimensions du contenu:", {
    //   width: contentWidth,
    //   height: contentHeight,
    // });

    const dataUrl = await domtoimage.toPng(canvasElement, {
      bgcolor: "#ffffff",
      quality: 1,
      style: {
        boxShadow: "none",
        margin: 0,
        padding: 0,
        background: "#ffffff",
      },
      cacheBust: true,
      useCORS: true,
      width: contentWidth,
      height: contentHeight,
      filter: () => true,
    });

    return dataUrl;
  } finally {
    // Restaurer l'état initial
    canvasElement.classList.remove("pdf-capture-mode");
    if (canvasElement) {
      canvasElement.style.backgroundColor =
        canvasElement.style.backgroundColor || "";
      canvasElement.style.height = canvasElement.style.height || "";
      canvasElement.style.overflow = canvasElement.style.overflow || "";
      canvasElement.style.maxHeight = canvasElement.style.maxHeight || "";
      canvasElement.style.position = canvasElement.style.position || "";
      canvasElement.style.top = canvasElement.style.top || "";
      canvasElement.style.left = canvasElement.style.left || "";
      canvasElement.style.transform = canvasElement.style.transform || "";
    }
    isPreviewMode.value = originalPreviewMode;
    if (!originalPreview) {
      supportStore.togglePreview();
    }
  }
};

const generatePdfBlob = async (): Promise<Blob> => {
  const dataUrl = await generateImage();

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  const img = new window.Image();
  img.src = dataUrl;

  await new Promise((resolve) => {
    img.onload = resolve;
  });

  const pageWidth = 210; // A4 width in mm
  const imgHeight = (img.height * pageWidth) / img.width;

  pdf.addImage(dataUrl, "PNG", 0, 0, pageWidth, imgHeight, undefined, "SLOW");

  return pdf.output("blob");
};

// Méthode pour générer et télécharger le PDF côté utilisateur
const generateAndDownloadPDF = async () => {
  try {
    const pdfBlob = await generatePdfBlob();
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${titleText.value || "emailing"}-${
      new Date().toISOString().split("T")[0]
    }.pdf`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    console.error("Erreur lors de la génération du PDF:", error);
    throw error;
  }
};

// Méthode alternative pour générer une image complète
const generateCompleteImage = async (): Promise<string> => {
  const canvasElement = document.querySelector(
    ".support-canvas"
  ) as HTMLElement;
  if (!canvasElement) {
    throw new Error("Élément canvas non trouvé");
  }

  // Sauvegarder l'état actuel
  const originalPreviewMode = isPreviewMode.value;
  const originalPreview = supportStore.isPreview;

  // Activer le mode aperçu pour la capture
  isPreviewMode.value = true;
  if (!originalPreview) {
    supportStore.togglePreview();
  }

  // Attendre que le DOM soit mis à jour
  await new Promise((resolve) => setTimeout(resolve, 500));

  try {
    // Créer un clone de l'élément pour la capture
    const clone = canvasElement.cloneNode(true) as HTMLElement;
    clone.style.position = "absolute";
    clone.style.top = "-9999px";
    clone.style.left = "-9999px";
    clone.style.width = canvasElement.offsetWidth + "px";
    clone.style.height = "auto";
    clone.style.overflow = "visible";
    clone.style.backgroundColor = "#ffffff";
    clone.classList.add("pdf-capture-mode");

    document.body.appendChild(clone);

    // Attendre que le clone soit rendu
    await new Promise((resolve) => setTimeout(resolve, 200));

    const dataUrl = await domtoimage.toPng(clone, {
      bgcolor: "#ffffff",
      quality: 1,
      style: {
        boxShadow: "none",
        margin: 0,
        padding: 0,
        background: "#ffffff",
      },
      cacheBust: true,
      useCORS: true,
      width: clone.offsetWidth,
      height: clone.scrollHeight,
      filter: () => true,
    });

    // Nettoyer le clone
    document.body.removeChild(clone);

    return dataUrl;
  } finally {
    isPreviewMode.value = originalPreviewMode;
    if (!originalPreview) {
      supportStore.togglePreview();
    }
  }
};

const generateHtmlEmail = async (): Promise<string> => {
  try {
    console.log("=== DÉBUT GÉNÉRATION HTML EMAIL ===");
    console.log("Timestamp:", new Date().toISOString());
    console.log("Environment:", import.meta.env.MODE);

    const canvasElement = document.querySelector(
      ".support-canvas"
    ) as HTMLElement;
    if (!canvasElement) {
      console.error("❌ ERREUR: Élément canvas non trouvé");
      throw new Error("Élément canvas non trouvé");
    }

    console.log("✅ Canvas élément trouvé:", {
      width: canvasElement.offsetWidth,
      height: canvasElement.offsetHeight,
      childElementCount: canvasElement.childElementCount
    });

    // Récupérer toutes les données de personnalisation
    console.log("📦 Étape 1/4: Récupération des données produits...");
    const products = supportStore.currentSupport?.products || [];
    const customizations: { [productId: number]: any } = {};

    console.log("Produits à traiter:", {
      count: products.length,
      products: products.map((p: any) => ({ id: p?.id, name: p?.name }))
    });

    // Filtrer les produits valides
    const validProducts = products.filter(
      (product: any) => product && product.id
    );
    console.log("✅ Produits valides:", {
      count: validProducts.length,
      validProducts: validProducts.map((p: any) => ({ id: p.id, name: p.name }))
    });

    console.log("🎨 Étape 2/4: Récupération des personnalisations...");
    validProducts.forEach((product: any, index: number) => {
      try {
        console.log(`[${index + 1}/${validProducts.length}] Traitement produit ${product.id}...`);
        const customization = productCustomizationStore.getCustomization(
          product.id
        );
        customizations[product.id] = customization;
        console.log(
          `✅ Personnalisation récupérée pour produit ${product.id}:`,
          {
            hasCustomization: !!customization,
            keys: Object.keys(customization || {})
          }
        );
      } catch (error) {
        console.error(
          `❌ Erreur personnalisation produit ${product.id}:`,
          error
        );
        customizations[product.id] =
          productCustomizationStore.getDefaultCustomization();
      }
    });

    console.log("✅ Personnalisations récupérées:", {
      productCount: Object.keys(customizations).length,
      customizations: Object.keys(customizations)
    });

    // Utiliser directement la fonction utilitaire qui gère le mode preview
    console.log("🔄 Étape 3/4: Génération HTML complète...");
    console.log("Paramètres de génération:", {
      title: titleText.value,
      brand,
      customizationCount: Object.keys(customizations).length
    });
    
    const startTime = performance.now();
    const htmlContent = await generateCompleteEmailHtml(
      canvasElement,
      titleText.value,
      brand,
      customizations
    );
    const endTime = performance.now();

    console.log("✅ Étape 4/4: HTML généré avec succès!", {
      duration: `${(endTime - startTime).toFixed(2)}ms`,
      htmlLength: htmlContent.length,
      hasStyles: htmlContent.includes('<style>'),
      hasCanvas: htmlContent.includes('support-canvas')
    });
    return htmlContent;
  } catch (error) {
    console.error("❌ ERREUR CRITIQUE - Génération HTML email:", {
      error: error,
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : 'Pas de stack trace',
      timestamp: new Date().toISOString(),
      supportId: supportStore.currentSupport?.id,
      brand
    });
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(`Erreur lors de la génération du HTML: ${errorMessage}`);
  }
};

// Méthode pour récupérer l'état actuel du composant
const getCurrentState = () => {
  // Mettre à jour les pages avec les produits convertis en éléments
  const products = supportStore.currentSupport?.products || [];
  const elements = products.map((product: any, index: number) => {
    if (!product || !product.id) return null;

    // Récupérer les données de personnalisation depuis le store
    const customization = productCustomizationStore.getCustomization(
      product.id
    );

    // console.log("Conversion produit vers élément:", product);
    // console.log("Données de personnalisation:", customization);

    return {
      type: "product",
      name: product.name || "",
      description: product.description || "",
      price_ht: product.price?.toString() || "0.00",
      price_ttc: product.price?.toString() || "0.00",
      price_old: "0.00",
      discount_mode: "0",
      hook: "",
      product_image_id: product.product_image_id || product.id || 0,
      product_image: product.image_name || product.image || "",
      element_settings: {
        // Inclure toutes les données de personnalisation
        removeBackground: customization.removeBackground,
        backgroundColor: customization.backgroundColor,
        showPrice: customization.showPrice,
        priceScale: customization.priceScale,
        priceSize: customization.priceSize,
        priceTTCSize: customization.priceTTCSize,
        pictogram: customization.pictogram,
        pictoScale: customization.pictoScale,
        brand: customization.brand,
        brandScale: customization.brandScale,
        header: customization.header,
        titleScale: customization.titleScale,
        position: customization.position,
        rotation: customization.rotation,
        zIndex: customization.zIndex,
      },
      position_x: 0,
      position_y: 0,
      width: 100,
      height: 100,
      rotation: 0,
      z_index: index,
      custom_styles: {},
      product_id: product.id,
      // Ajouter les champs requis pour éviter les erreurs de validation
      pictogram_id: customization.pictogram?.id || null,
      brand_id: customization.brand?.id || null,
    };
  });

  // Mettre à jour les pages avec les éléments
  const updatedPages = supportStore.currentSupport?.pages.map(
    (page: any, pageIndex: number) => {
      if (pageIndex === 0) {
        // Seulement la première page pour les emailings
        return {
          ...page,
          elements: elements,
        };
      }
      return page;
    }
  );

  return {
    name: titleText.value,
    dates: validityText.value,
    pages: updatedPages,
    repiquage: selectedRepiquage.value,
  };
};

// Méthode pour générer et télécharger le HTML complet
const generateAndDownloadHtml = async () => {
  try {
    const htmlContent = await generateHtmlEmail();

    // Créer un blob avec le HTML généré
    const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    // Créer un lien de téléchargement
    const link = document.createElement("a");
    link.href = url;
    link.download = `email-${titleText.value || "agco"}-${
      new Date().toISOString().split("T")[0]
    }.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Nettoyer l'URL
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);

    // Afficher une notification de succès
    // console.log("HTML généré et téléchargé avec succès");
    alert("HTML généré et téléchargé avec succès !");
  } catch (error) {
    console.error("Erreur lors de la génération du HTML:", error);
    alert("Erreur lors de la génération du HTML");
  }
};

// Composant de fallback pour éviter les erreurs de rendu
const ProductBlockFallback = {
  name: "ProductBlockFallback",
  template: `
    <div class="product-block-fallback">
      <div class="text-center text-gray-500 py-8">
        <p>Chargement du produit...</p>
      </div>
    </div>
  `,
};

// Exposer les méthodes pour utilisation externe
defineExpose({
  generateImage,
  generateCompleteImage,
  generatePdfBlob,
  generateAndDownloadPDF,
  generateHtmlEmail,
  generateAndDownloadHtml,
  getCurrentState,
  titleText,
  validityText,
});
</script>

<script lang="ts">
export default {
  name: "SupportCanvas",
};
</script>

<style scoped>
.support-canvas {
  width: 80%;
  min-height: 297mm;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background: white;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transform-origin: top left;
  transition: transform 0.3s ease;
}

.support-content {
  flex: 1;
  padding: 1.5rem;
  min-height: 200px;
  overflow: visible;
}

.products-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
}

.product-zone {
  border: 2px dashed #ccc;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow: visible;
  min-height: 200px;
  width: 100%;
  padding: 1rem;
}

.product-zone:hover {
  border-color: #b70018;
}

.drop-active {
  border-color: #b70018;
  background-color: rgba(183, 0, 24, 0.1);
}

.product-zone.has-product {
  border-style: solid;
}

.product-zone.selected {
  background-color: #fff8dc;
}

.btn-control {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  background-color: white;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-control:hover:not(:disabled) {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

.btn-control:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.preview-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background-color: white;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.preview-toggle:hover {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

.preview-toggle.active {
  background-color: #b70018;
  border-color: #b70018;
  color: white;
}

.preview-toggle .icon {
  font-size: 1.2rem;
}

.support-header {
  border: 2px dashed #ccc;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
}

.support-header.drop-active {
  border-color: #b70018;
  background-color: rgba(183, 0, 24, 0.1);
}

.support-title-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.support-title-text {
  outline: none;
}

.color-picker-toggle {
  background-color: #f9fafb;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.color-picker-toggle:hover {
  background-color: #f3f4f6;
  border-color: #9ca3af;
}

.color-picker-toggle:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px rgba(183, 0, 24, 0.5);
}

.color-picker-toggle:active {
  background-color: #e5e7eb;
}

.hidden-color-input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  pointer-events: none;
}

.preview-mode .color-picker-toggle {
  display: none !important;
}

.preview-mode .support-header {
  border: none !important;
}

/* Mode aperçu */
.preview-mode .product-zone {
  border: none !important;
  padding: 0 !important;
  box-shadow: none !important;
}

.preview-mode .product-zone:hover {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

.preview-mode .drop-active {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

.preview-mode .support-title-container {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.preview-mode .support-title-text {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.empty-product-zone {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  border: 2px dashed #e5e7eb;
  border-radius: 0.5rem;
  background-color: #f9fafb;
  transition: all 0.3s ease;
}

.empty-product-zone:hover {
  border-color: #b70018;
  background-color: rgba(183, 0, 24, 0.05);
}

.product-block-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  border: 2px dashed #e5e7eb;
  border-radius: 0.5rem;
  background-color: #f9fafb;
  transition: all 0.3s ease;
}

.preview-mode .support-canvas {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.preview-mode .support-content {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.preview-mode .support-footer {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.preview-mode .support-header {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.preview-mode .preview-mode-button {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  background: transparent !important;
}

@media print {
  .support-canvas {
    box-shadow: none;
    margin: 0;
    padding: 0;
  }

  .product-zone {
    border: none !important;
    padding: 0;
  }

  .support-header,
  .support-footer {
    position: fixed;
    width: 100%;
    background-color: white;
    z-index: 100;
  }

  .support-header {
    top: 0;
  }

  .support-footer {
    bottom: 0;
  }

  .support-content {
    margin: 80px 0;
  }
}

/* Styles pour la modale */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Styles pour le slider de zoom */
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  outline: none;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background: #b70018;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.15s ease-in-out;
}

input[type="range"]::-webkit-slider-thumb:hover {
  background: #8c0014;
}

input[type="range"]::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: #b70018;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.15s ease-in-out;
  border: none;
}

input[type="range"]::-moz-range-thumb:hover {
  background: #8c0014;
}

/* Styles pour le mode de capture PDF */
.pdf-capture-mode {
  box-shadow: none !important;
  margin: 0 !important;
  padding: 0 !important;
  height: auto !important;
  overflow: visible !important;
  min-height: auto !important;
  max-height: none !important;
  position: static !important;
  top: auto !important;
  left: auto !important;
  transform: none !important;
  display: block !important;
  border: none !important;
  outline: none !important;
}

.pdf-capture-mode .product-zone {
  border: none !important;
  padding: 0 !important;
  display: block !important;
  height: auto !important;
  overflow: visible !important;
  box-shadow: none !important;
  outline: none !important;
}

.pdf-capture-mode .support-header,
.pdf-capture-mode .support-footer {
  position: static !important;
  width: 100% !important;
  background-color: white !important;
  z-index: auto !important;
  display: block !important;
  height: auto !important;
  overflow: visible !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.pdf-capture-mode .support-content {
  margin: 0 !important;
  height: auto !important;
  overflow: visible !important;
  display: block !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.pdf-capture-mode .support-canvas {
  height: auto !important;
  min-height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  position: static !important;
  display: block !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.pdf-capture-mode .products-container {
  height: auto !important;
  overflow: visible !important;
  display: block !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.pdf-capture-mode .support-footer {
  display: block !important;
  height: auto !important;
  overflow: visible !important;
  position: static !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.pdf-capture-mode .support-title-container {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.pdf-capture-mode .support-title-text {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}
</style>
